import {css, Typography, useTheme} from '@mui/material';
import {isNumber, isObject} from 'lodash';
import * as React from 'react';

import {DlMuiPopper} from './DlMuiPopper';
import {mixins} from 'src/js/common/jsStyles/mixins';
import {GetStylesFunction} from 'src/js/common/types';
import {useMuiAnchor} from 'src/js/hooks';

interface OwnProps {
  pageTo?: number;
  disabled?: boolean;
  onClick?: (page: number) => void;
  title?: string;
  children: React.ReactNode;
  eventKey?: any;
  isBig?: boolean;
  dataName?: string;
}

type Props = OwnProps;

export const FixedPaginationButton: React.FC<Props> = (props) => {
  const {disabled = false, onClick, pageTo, title, children, eventKey, isBig, dataName} = props;
  const theme = useTheme();
  const styles = getStyles({theme, params: {disabled, isBig}});

  const hasTooltip = !!title;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);

  const handleKeyDown = (event: {key: string; stopPropagation: () => void}) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
      if (onClick && !disabled) {
        onClick(pageTo || eventKey);
      }
    }
    return true;
  };

  const handleClick = (eventObject: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && onClick) {
      if (isObject(eventObject) && isNumber(pageTo)) {
        onClick(pageTo);
      }
    }
  };

  return (
    <>
      <div
        css={styles.dlPaginationButton}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        data-name={dataName}
      >
        {children}
      </div>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{title}</Typography>
      </DlMuiPopper>
    </>
  );
};

FixedPaginationButton.displayName = 'FixedPaginationButton';

const getStyles: GetStylesFunction = (options) => {
  let background;
  background = options?.params?.disabled
    ? options?.theme?.palette.custom.fixedPaginationButtonBgColor
    : options?.theme?.palette.custom.fixedPaginationButtonBgColor;
  if (options?.params?.isBig) background = 'none';
  let color;
  if (options?.params?.disabled) {
    color = options?.params?.isBig
      ? options?.theme?.palette.custom.fixedPaginationButtonIconDisabledBig
      : options?.theme?.palette.custom.fixedPaginationButtonIconDisabled;
  } else {
    color = options?.params?.isBig
      ? options?.theme?.palette.custom.fixedPaginationButtonIconBig
      : options?.theme?.palette.custom.fixedPaginationButtonIcon;
  }
  const common = `
    width: ${options?.params?.isBig ? 32 : 24}px;
    height: ${options?.params?.isBig ? 32 : 24}px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    ${mixins.dlButtonAsLink};
    ${mixins.dlPageLink};
    border: 1px solid ${options?.theme?.palette.custom.fixedPaginationButtonIconBorder as string};
    border-width: ${options?.params?.isBig ? 1 : 0}px;
    border-radius: ${options?.params?.isBig ? 8 : 0}px;
    background-color: ${background};
    color: ${color};
  `;
  return {
    dlPaginationButton: options?.params?.disabled
      ? css`
          ${common}

          &,
          &:hover,
          &:focus,
          &:active:focus,
          &:active:hover {
            cursor: not-allowed;
          }
        `
      : css`
          ${common}

          &:hover {
            cursor: pointer;
          }
        `,
  };
};
